import { useState } from "react";

import { UserAvatar } from "@/components/user";
import { useCurrentUser, useLogout } from "@/contexts";
import { env } from "@/env";
import { useImpersonationSession } from "@/hooks/common";
import { useGetUserRoleLabel } from "@/hooks/user";
import { useT } from "@/i18n";
import { trackAction } from "@/lib/sentry";
import {
  Alert,
  Button,
  DialogTrigger,
  IconExit,
  IconUserAvatar,
  IconUserSettings,
  NavLink,
  PointIndicator,
  PopoverOrTray,
  UnstyledButton,
} from "@/ui";

import { useGetNavLinkPath, useIsNavLinkActive } from "../NavLink";

export const UserProfileDropdown = () => {
  const t = useT();
  const currentUser = useCurrentUser();
  const logout = useLogout();
  const getUserRoleLabel = useGetUserRoleLabel();
  const getNavLinkPath = useGetNavLinkPath();
  const isNavLinkActive = useIsNavLinkActive();
  const { isImpersonating, formattedRemaining, endSession } = useImpersonationSession();
  const [isOpen, setIsOpen] = useState(false);

  const fullName = [currentUser.name, currentUser.surname].filter(Boolean).join(" ");
  const userSettingsPath = getNavLinkPath("userSettings");
  const profileUrl = `${env.clientUrl}/profile/${currentUser.internalId}`;
  const isUserSettingsActive = isNavLinkActive({ type: "userSettings" });

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <UnstyledButton
        aria-label={
          isImpersonating
            ? `${t("user.actions.profile")} (${t("user.impersonation.session.indicatorLabel")})`
            : t("user.actions.profile")
        }
        className="relative rounded-full cursor-pointer outline-hidden ring-focus ring-offset-2 ring-offset-red/0 focus-visible:ring-2"
      >
        <UserAvatar user={currentUser} width={32} height={32} />
        {isImpersonating && (
          <span aria-hidden className="absolute -top-0.5 -right-0.5">
            <PointIndicator color="danger" size={10} />
          </span>
        )}
      </UnstyledButton>
      <PopoverOrTray
        trayProps={{
          size: "auto",
          "aria-label": t("user.actions.profile"),
        }}
        popoverProps={{
          className: "p-[10px] w-[252px]",
          "aria-label": t("user.actions.profile"),
        }}
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex items-center gap-[10px] w-full">
            <UserAvatar user={currentUser} width={32} height={32} />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="preset-label-md text-new-primary truncate">{fullName}</span>
              {currentUser.role && (
                <span className="preset-body-md text-secondary truncate">
                  {getUserRoleLabel(currentUser.role)}
                </span>
              )}
            </div>
          </div>
          <div>
            <NavLink
              href={profileUrl}
              title={t("user.actions.profile")}
              startIcon={IconUserAvatar}
              isActive={false}
              onPress={() =>
                trackAction("dashboard.header.userProfile.profile.open", {
                  userId: currentUser.id,
                })
              }
            />
            <NavLink
              href={userSettingsPath}
              title={t("user.actions.settings")}
              startIcon={IconUserSettings}
              isActive={isUserSettingsActive}
              onPress={() =>
                trackAction("dashboard.header.userProfile.settings.open", {
                  userId: currentUser.id,
                })
              }
            />
          </div>
        </div>
        {isImpersonating && (
          <Alert
            variant="warning"
            hideCloseButton
            className="mt-[10px]"
            title={t("user.impersonation.session.indicatorLabel")}
            description={t("user.impersonation.session.alertDescription", {
              time: formattedRemaining,
            })}
          />
        )}
        {isImpersonating ? (
          <Button
            variant="secondary"
            color="danger"
            className="w-full mt-[10px]"
            startIcon={IconExit}
            onPress={() => {
              trackAction("dashboard.header.userProfile.impersonation.end", {
                userId: currentUser.id,
              });
              endSession();
            }}
          >
            {t("user.impersonation.session.endSession")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            color="muted"
            className="w-full mt-[10px]"
            startIcon={IconExit}
            onPress={() => {
              trackAction("dashboard.header.userProfile.logout", {
                userId: currentUser.id,
              });
              logout();
            }}
          >
            {t("user.actions.logout")}
          </Button>
        )}
      </PopoverOrTray>
    </DialogTrigger>
  );
};
